import React, { useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import "../auth.css";

/* ─── Icons ─────────────────────────────────────────────── */
const IconLockReset = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    <line x1="12" y1="15" x2="12" y2="17"/>
  </svg>
);
const IconMail = () => (
  <svg className="auth-input-icon" viewBox="0 0 24 24">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconLock = () => (
  <svg className="auth-input-icon" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const OTP_LENGTH = 6;

function ConfirmOTP() {
  const location  = useLocation();
  const email     = location.state?.email || "";
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [form]    = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [digits,  setDigits]  = useState(Array(OTP_LENGTH).fill(""));
  const refs = useRef([]);

  const handleDigit = (idx, val) => {
    const char = val.replace(/\D/, "").slice(-1);
    const next = [...digits];
    next[idx] = char;
    setDigits(next);
    if (char && idx < OTP_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) refs.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = [...digits];
    pasted.split("").forEach((c, i) => { next[i] = c; });
    setDigits(next);
    refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const onFinish = async (values) => {
    const otp = digits.join("");
    if (otp.length < OTP_LENGTH) { toast.error("Please enter the full 6-digit code"); return; }
    setLoading(true);
    try {
      dispatch(showLoading());
      const response = await axios.post("/api/reset-Password/reset-password", { ...values, otp });
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success("Password changed successfully");
        navigate("/login");
      } else {
        toast.error(response.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-cloud auth-cloud-1" />
      <div className="auth-cloud auth-cloud-2" />
      <div className="auth-cloud auth-cloud-3" />

      <div className="auth-card">
        <div className="auth-icon-wrap">
          <IconLockReset />
        </div>

        <h1 className="auth-title">Set a new password</h1>
        <p className="auth-subtitle">
          Enter the code we sent to your email, then choose a strong new password.
        </p>

        <Form form={form} layout="vertical" onFinish={onFinish} className="auth-form">
          {/* Disabled email */}
          <Form.Item name="email" initialValue={email}>
            <div className="auth-input-wrap">
              <IconMail />
              <Input disabled size="large" />
            </div>
          </Form.Item>

          {/* OTP boxes */}
          <div>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Enter 6-digit OTP code
            </p>
            <div className="otp-row" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (refs.current[i] = el)}
                  className={`otp-box${d ? " filled" : ""}`}
                  value={d}
                  maxLength={1}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => handleDigit(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                />
              ))}
            </div>
          </div>

          {/* New passwords side by side */}
          <div className="auth-row">
            <Form.Item name="password"
              rules={[{ required: true, message: "Required" }]}>
              <div className="auth-input-wrap">
                <IconLock />
                <Input.Password
                  placeholder="New password"
                  size="large"
                  iconRender={(v) => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                />
              </div>
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                    return Promise.reject(new Error("Passwords don't match"));
                  },
                }),
              ]}
            >
              <div className="auth-input-wrap">
                <IconLock />
                <Input.Password
                  placeholder="Confirm"
                  size="large"
                  iconRender={(v) => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                />
              </div>
            </Form.Item>
          </div>

          <Form.Item>
            <Button htmlType="submit" loading={loading} className="auth-btn">
              {loading ? "Updating password…" : "Change Password"}
            </Button>
          </Form.Item>
        </Form>

        <Link to="/login" className="auth-back">
          <IconArrowLeft /> Back to login
        </Link>
      </div>
    </div>
  );
}

export default ConfirmOTP;